import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectDarkMode } from '../../../app/store/slices/uiSlice';
import { ChildrenService } from '../../../shared/services/childrenService';
import {
  ClipboardDocumentCheckIcon,
  CheckCircleIcon,
  MagnifyingGlassIcon,
  ArrowPathIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';
import {
  Table,
  TableHead,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  TableEmptyState,
  TableBodySkeleton,
  Pagination,
} from '../../../shared/components/ui';

const formatDate = (dateString) => {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
};

const STATUS_STYLES = {
  UPCOMING: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  DUE: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  OVERDUE: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  COMPLETED: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
};

const PlanStatusBadge = ({ status }) => (
  <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${STATUS_STYLES[status] || 'bg-gray-100 text-gray-600'}`}>
    {status}
  </span>
);

const STATUS_OPTIONS = ['', 'DUE', 'OVERDUE', 'UPCOMING', 'COMPLETED'];

export default function ChildVaccinationPlansListPage() {
  const navigate = useNavigate();
  const darkMode = useSelector(selectDarkMode);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 15, total: 0, totalPages: 1 });

  const [filters, setFilters] = useState({ status: '', childName: '', fromDate: '', toDate: '' });
  const [applied, setApplied] = useState({ status: '', childName: '', fromDate: '', toDate: '' });


  const load = useCallback(async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const params = { page, limit: pagination.limit };
      if (applied.status) params.status = applied.status;
      if (applied.childName) params.childName = applied.childName;
      if (applied.fromDate) params.fromDate = applied.fromDate;
      if (applied.toDate) params.toDate = applied.toDate;

      const res = await ChildrenService.listVaccinationPlans(params);
      const data = res?.data ?? res;
      setItems(Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : []);
      if (data?.pagination) {
        setPagination(prev => ({
          ...prev,
          page: data.pagination.page ?? page,
          total: data.pagination.total ?? 0,
          totalPages: data.pagination.totalPages ?? 1,
        }));
      }
    } catch (err) {
      setError(err?.response?.data?.message ?? 'Failed to load vaccination plans.');
    } finally {
      setLoading(false);
    }
  }, [applied, pagination.limit]);

  useEffect(() => { load(1); }, [applied]);

  const handleApplyFilters = () => {
    setApplied({ ...filters });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handleClearFilters = () => {
    const empty = { status: '', childName: '', fromDate: '', toDate: '' };
    setFilters(empty);
    setApplied(empty);
  };

  const hasActiveFilters = applied.status || applied.childName || applied.fromDate || applied.toDate;

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-gray-900' : 'bg-gray-50'} p-4 lg:p-6`}>
      {/* Header */}
      <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className={`p-2.5 rounded-xl ${darkMode ? 'bg-green-900/30' : 'bg-green-100'}`}>
            <ClipboardDocumentCheckIcon className={`w-7 h-7 ${darkMode ? 'text-green-400' : 'text-green-600'}`} />
          </div>
          <div>
            <h1 className={`text-2xl font-bold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
              Child Vaccination Plans
            </h1>
            <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
              Age-based schedule across all children
            </p>
          </div>
        </div>
        <button
          type="button"
          onClick={() => load(pagination.page)}
          disabled={loading}
          className={`inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium disabled:opacity-50 ${darkMode ? 'border-gray-700 bg-gray-800 text-gray-200 hover:bg-gray-700' : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'}`}
        >
          <ArrowPathIcon className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* Filters */}
      <div className={`mb-5 rounded-xl border p-4 ${darkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
        <div className="flex items-center gap-2 mb-3">
          <FunnelIcon className="h-4 w-4 text-[#4057fe]" />
          <span className={`text-sm font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>Filters</span>
          {hasActiveFilters && (
            <button
              type="button"
              onClick={handleClearFilters}
              className="ml-auto text-xs text-red-500 hover:text-red-700"
            >
              Clear all
            </button>
          )}
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
          <div className="relative">
            <MagnifyingGlassIcon className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Child name..."
              value={filters.childName}
              onChange={(e) => setFilters(f => ({ ...f, childName: e.target.value }))}
              className={`w-full rounded-lg border pl-8 pr-3 py-2 text-sm outline-none focus:border-[#4057fe] focus:ring-2 focus:ring-[#4057fe]/20 ${darkMode ? 'border-gray-600 bg-gray-700 text-white placeholder-gray-400' : 'border-gray-200 bg-gray-50 text-gray-900'}`}
            />
          </div>
          <select
            value={filters.status}
            onChange={(e) => setFilters(f => ({ ...f, status: e.target.value }))}
            className={`w-full rounded-lg border px-3 py-2 text-sm outline-none focus:border-[#4057fe] ${darkMode ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-200 bg-gray-50 text-gray-900'}`}
          >
            <option value="">All statuses</option>
            {STATUS_OPTIONS.filter(Boolean).map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <input
            type="date"
            placeholder="From date"
            value={filters.fromDate}
            onChange={(e) => setFilters(f => ({ ...f, fromDate: e.target.value }))}
            className={`w-full rounded-lg border px-3 py-2 text-sm outline-none focus:border-[#4057fe] ${darkMode ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-200 bg-gray-50 text-gray-900'}`}
          />
          <input
            type="date"
            placeholder="To date"
            value={filters.toDate}
            onChange={(e) => setFilters(f => ({ ...f, toDate: e.target.value }))}
            className={`w-full rounded-lg border px-3 py-2 text-sm outline-none focus:border-[#4057fe] ${darkMode ? 'border-gray-600 bg-gray-700 text-white' : 'border-gray-200 bg-gray-50 text-gray-900'}`}
          />
        </div>
        <div className="mt-3 flex justify-end">
          <button
            type="button"
            onClick={handleApplyFilters}
            className="inline-flex items-center gap-2 rounded-lg bg-[#4057fe] px-4 py-2 text-sm font-medium text-white hover:bg-[#3347e0]"
          >
            Apply Filters
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-5 p-4 bg-red-100 border border-red-300 text-red-700 rounded-lg text-sm">
          {error}
        </div>
      )}

      {/* Table */}
      <Table>
        <TableHead>
          <tr>
            <TableHeaderCell>Child</TableHeaderCell>
            <TableHeaderCell>Vaccine</TableHeaderCell>
            <TableHeaderCell>Dose</TableHeaderCell>
            <TableHeaderCell>Age Group</TableHeaderCell>
            <TableHeaderCell>Scheduled Window</TableHeaderCell>
            <TableHeaderCell>Status</TableHeaderCell>
            <TableHeaderCell>Completed Date</TableHeaderCell>
            <TableHeaderCell>Action</TableHeaderCell>
          </tr>
        </TableHead>
        <TableBody>
          {loading ? (
            <TableBodySkeleton rows={8} columns={8} />
          ) : items.length > 0 ? (
            items.map((item) => (
              <TableRow key={item.id}>
                <TableCell>
                  <button
                    type="button"
                    onClick={() => navigate(`/admin/children/${item.childId}/vaccination-plan`)}
                    className="text-sm font-medium text-[#4057fe] hover:underline"
                  >
                    {item.childName}
                  </button>
                </TableCell>
                <TableCell>
                  <span className={`text-sm font-medium ${darkMode ? 'text-white' : 'text-gray-900'}`}>
                    {item.vaccineName}
                  </span>
                </TableCell>
                <TableCell>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${darkMode ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-100 text-blue-800'}`}>
                    Dose {item.doseNumber}
                  </span>
                </TableCell>
                <TableCell>
                  <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {item.ageGroupLabel}
                  </span>
                </TableCell>
                <TableCell>
                  <span className={`text-sm ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {formatDate(item.scheduledFromDate)} – {formatDate(item.scheduledToDate)}
                  </span>
                </TableCell>
                <TableCell>
                  <PlanStatusBadge status={item.status} />
                </TableCell>
                <TableCell>
                  {item.completedDate
                    ? <span className={`text-sm ${darkMode ? 'text-green-400' : 'text-green-600'}`}>{formatDate(item.completedDate)}</span>
                    : <span className={`text-sm ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>—</span>
                  }
                </TableCell>
                <TableCell>
                  {(item.status === 'DUE' || item.status === 'OVERDUE') && (
                    <button
                      type="button"
                      onClick={() => navigate('/admin/Appointments/add')}
                      className="inline-flex items-center gap-1.5 rounded-lg bg-green-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-green-700"
                    >
                      <CheckCircleIcon className="h-3.5 w-3.5" />
                      Add Appointment
                    </button>
                  )}
                </TableCell>
              </TableRow>
            ))
          ) : (
            <TableEmptyState
              icon={ClipboardDocumentCheckIcon}
              title="No results found"
              message={hasActiveFilters ? 'Try adjusting your filters.' : 'No vaccination plan items found.'}
            />
          )}
        </TableBody>
      </Table>

      {pagination.totalPages > 1 && (
        <div className="mt-6">
          <Pagination
            currentPage={pagination.page}
            totalPages={pagination.totalPages}
            totalItems={pagination.total}
            itemsPerPage={pagination.limit}
            onPageChange={(p) => { setPagination(prev => ({ ...prev, page: p })); load(p); }}
            itemName="plan item"
            isLoading={loading}
          />
        </div>
      )}
    </div>
  );
}
